/**
 * How the app finds out that something has arrived without anybody asking it
 * to look.
 *
 * The flags this covers — the rail's badges, the "New" tag on a row, the dot
 * on a client whose pre-consultation answers have come back — are all news,
 * and news that only appears on a hard refresh is not news. A practitioner
 * with the client list open while their client fills the form in should watch
 * the dot arrive, not find it tomorrow.
 *
 * There is no push side to the API: no socket, no stream, nothing on the wire
 * that says "a form came back". So the reads that carry news poll, on a fixed
 * interval, and the interval is here rather than in each hook so the rail and
 * the list it leads to cannot drift apart and disagree.
 *
 * A minute, which is a compromise made in both directions: short enough that
 * something arriving feels like it arrived, long enough that a practitioner
 * who leaves a tab open all afternoon costs the backend sixty requests an hour
 * per polled read rather than six hundred.
 *
 * `refetchIntervalInBackground` is left at its default of `false` on purpose:
 * a hidden tab stops asking entirely, and the refetch-on-focus TanStack Query
 * already does is what brings it up to date the moment it is looked at again.
 * So a dozen forgotten tabs cost nothing, and the one in front of somebody is
 * the one that polls.
 */

/** The poll, in milliseconds. */
export const NEWS_REFRESH_MS = 60_000;

/**
 * What a badge shows above, rather than a number nobody reads.
 *
 * Past a hundred the digit has stopped being the news: "127 enquiries nobody
 * has opened" and "99+" prompt the same act, and the second one can be read
 * without stopping. Shared by every badge on the rail so two of them cannot
 * disagree about where a number stops being useful.
 */
export const MAX_SHOWN_COUNT = 99;

/**
 * What a query carrying news adds to its own options.
 *
 * `staleTime` matches the interval rather than being left at zero: without it
 * every mount of a screen — every walk from the list to a client and back —
 * fires a fresh read on top of the polling one, which is a burst of requests
 * that tells nobody anything the poll was not about to say.
 */
export const liveNewsOptions = {
  refetchInterval: NEWS_REFRESH_MS,
  staleTime: NEWS_REFRESH_MS,
} as const;
